import React, { useState } from "react";
import { Search } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { StatusBadge } from "../shared/StatusBadge";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";

const VERDICT_META = {
  delivered: { label: "Delivered", variant: "success" },
  not_eligible: { label: "Not eligible", variant: "default" },
  skipped: { label: "Skipped", variant: "warning" },
  not_generated: { label: "Eligible — not generated", variant: "warning" },
  not_assigned: { label: "Generated — not assigned", variant: "warning" },
  manually_removed: { label: "Manually removed", variant: "danger" },
  assigned_not_delivered: { label: "Assigned — not delivered", variant: "danger" },
  unknown: { label: "Unknown / insufficient evidence", variant: "default" },
};

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-3 py-1 text-sm">
      <span className="text-text-muted">{label}</span>
      <span className="text-right font-medium text-text-main">{value}</span>
    </div>
  );
}

export function DeliveryInvestigation({ customerId }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const run = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const { data } = await apiClient.get(`/customers/${customerId}/delivery-investigation?date=${date}`);
      setResult(data);
    } catch (e) {
      setError(getApiErrorMessage(e, "Could not run the investigation."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="delivery-investigation">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="mb-1 block text-xs text-text-muted">Delivery date to investigate</label>
          <Input type="date" className="h-9 w-48" value={date} onChange={(e) => setDate(e.target.value)} data-testid="investigation-date" />
        </div>
        <Button variant="accent" onClick={run} disabled={loading} data-testid="investigation-run">
          <Search size={15} strokeWidth={1.5} /> {loading ? "Investigating…" : "Why wasn't this tiffin delivered?"}
        </Button>
      </div>

      {error && <p className="text-sm text-danger">{error}</p>}

      {result && (
        <div className="space-y-4">
          <p className="text-xs text-text-muted" data-testid="investigation-note">{result.note}</p>
          {result.findings.map((f, i) => {
            const m = VERDICT_META[f.verdict] || VERDICT_META.unknown;
            const ev = f.evidence || {};
            return (
              <div key={i} className="rounded-lg border border-border bg-surface p-4" data-testid={`investigation-finding-${i}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <StatusBadge label={m.label} variant={m.variant} />
                  {ev.subscription_id && <span className="text-xs text-text-muted">Subscription #{ev.subscription_id}</span>}
                </div>
                <p className="mt-2 text-sm text-text-main">{f.detail}</p>
                <div className="mt-3 rounded-md bg-background p-3">
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-text-muted">Current record evidence</p>
                  <Row label="Subscription status (now)" value={ev.subscription_status || "—"} />
                  <Row label={`Scheduled on ${result.weekday}?`} value={ev.scheduled_delivery_day ? "Yes" : "No"} />
                  <Row label="Within active window" value={ev.within_subscription_window ? "Yes" : "No"} />
                  <Row label="Paused / Skipped" value={ev.paused ? "Paused" : ev.skipped ? "Skipped" : "No"} />
                  <Row label="Delivery record exists" value={ev.delivery_record_exists ? "Yes" : "No"} />
                  <Row label="Delivery status" value={ev.delivery_status || "—"} />
                  <Row label="On a route" value={ev.route_id ? `Route #${ev.route_id}` : "No"} />
                </div>
                {Array.isArray(ev.manual_audit_events) && ev.manual_audit_events.length > 0 && (
                  <div className="mt-3">
                    <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-text-muted">Recorded manual actions</p>
                    {ev.manual_audit_events.map((e, j) => (
                      <p key={j} className="text-xs text-text-muted">
                        {e.action} by {e.actor_username || "system"}{e.reason ? ` — ${e.reason}` : ""}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
